import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { GripVertical, Trash2, Plus, Eye, EyeOff, ArrowUp, ArrowDown } from "lucide-react";
import { inputCls, btn } from "@/components/admin/ui";

const LAYOUTS = ["landscape", "reels"];
const isSection = (c) => (c.section_type || "filter") !== "filter";

const CategoryRow = ({ category: c, onMove, onUpdate, onDelete }) => (
  <div className={`flex items-center gap-2 bg-surface border border-hairline rounded-lg px-3 py-2.5 ${c.hidden ? "opacity-50" : ""}`}>
    <div className="flex flex-col">
      <button onClick={() => onMove(-1)} className="text-mute hover:text-txt"><ArrowUp size={13} /></button>
      <button onClick={() => onMove(1)} className="text-mute hover:text-txt"><ArrowDown size={13} /></button>
    </div>
    <input
      defaultValue={c.label}
      onBlur={(e) => e.target.value !== c.label && onUpdate({ label: e.target.value })}
      className={`${inputCls} flex-1 min-w-0`}
    />
    {isSection(c) ? (
      <>
        <input
          defaultValue={c.eyebrow || ""}
          placeholder="eyebrow"
          onBlur={(e) => e.target.value !== (c.eyebrow || "") && onUpdate({ eyebrow: e.target.value })}
          className={`${inputCls} !w-28 shrink-0`}
          data-testid={`section-eyebrow-${c.slug}`}
        />
        <span className="shrink-0 font-mono text-[10px] uppercase tracking-wide text-lav border border-lav/40 rounded-full px-2 py-1">section</span>
      </>
    ) : (
      <select value={c.layout} onChange={(e) => onUpdate({ layout: e.target.value })} className={`${inputCls} !w-32 shrink-0`}>
        {LAYOUTS.map((l) => <option key={l} value={l}>{l}</option>)}
      </select>
    )}
    <button onClick={() => onUpdate({ hidden: !c.hidden })} className="text-mute hover:text-txt p-1">{c.hidden ? <EyeOff size={15} /> : <Eye size={15} />}</button>
    <button onClick={onDelete} className="text-mute hover:text-red-400 p-1"><Trash2 size={15} /></button>
  </div>
);

export default function SectionsTab({ categories, reload }) {
  const [list, setList] = useState(categories);
  useEffect(() => setList(categories), [categories]);

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    setList(next);
    await api.put("/categories/reorder", { ids: next.map((c) => c.id) });
    reload();
  };

  const update = async (c, patch) => { await api.put(`/categories/${c.id}`, patch); reload(); };

  const del = async (c) => {
    if (!window.confirm(`Delete category "${c.label}"? Projects keep their slug.`)) return;
    await api.delete(`/categories/${c.id}`); reload();
  };

  const add = async () => {
    const label = window.prompt("New category name?");
    if (!label) return;
    await api.post("/categories", { label, slug: label.toLowerCase().replace(/\s+/g, "-"), layout: "landscape" });
    reload();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="font-mono text-xs text-mute">Filter categories & sections. "reels" layout = vertical phone-feed.</p>
        <button onClick={add} className={`${btn} bg-lav text-ink flex items-center gap-2`} data-testid="add-category"><Plus size={14} /> Add</button>
      </div>
      <div className="space-y-2">
        {list.map((c, i) => (
          <CategoryRow
            key={c.id}
            category={c}
            onMove={(dir) => move(i, dir)}
            onUpdate={(patch) => update(c, patch)}
            onDelete={() => del(c)}
          />
        ))}
      </div>
    </div>
  );
}
